import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Lock, Mail, X } from 'lucide-react';
import './ConfirmActionModal.css';
import { apiPost } from '../lib/api';

/**
 * Combined typed-confirmation + step-up re-auth modal (H4 + M7).
 *
 * Supports TWO step-up methods:
 *   - Password (for email+password users)
 *   - 6-digit email code (for Google-OAuth-only users, or as fallback)
 *
 * Props:
 *  - open, title, message, confirmWord, destructive, confirmLabel, onClose, onConfirm
 *  - hasPassword: boolean — whether the current user has a password_hash. If
 *                 false, the modal defaults to email-code mode. If true, the
 *                 user can still switch to code mode via a link.
 *  - requirePassword: boolean — whether step-up is needed at all (default true)
 *  - requireTypedText: boolean — when false, the typed-text confirmation step
 *                      is hidden entirely (default true). Use for low-risk
 *                      confirmations where typing a word is overkill.
 */
export default function ConfirmActionModal({
  open,
  title,
  message,
  confirmWord = 'CONFIRM',
  requireTypedText = true,
  destructive = true,
  requirePassword = true,
  hasPassword = true,
  confirmLabel = 'Confirm',
  onClose,
  onConfirm,
}) {
  const [mode, setMode] = useState(hasPassword ? 'password' : 'code');
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [stepUpOk, setStepUpOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMode(hasPassword ? 'password' : 'code');
      setTyped('');
      setPassword('');
      setCode('');
      setCodeSent(false);
      setStepUpOk(false);
      setBusy(false);
      setErr(null);
      setTimeout(() => firstInputRef.current?.focus(), 60);
    }
  }, [open, hasPassword]);

  // Close on Escape — matches every other modal/sheet in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const typedOk = !requireTypedText || (typed.trim().toUpperCase() === confirmWord.trim().toUpperCase());
  const readyToConfirm = typedOk && (!requirePassword || stepUpOk);

  const sendCode = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await apiPost('/api/auth/step-up/send-code');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not send code');
      setCodeSent(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleStepUp = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body = mode === 'password' ? { password } : { code };
      const res = await apiPost('/api/auth/step-up/verify', body);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Re-authentication failed');
      setStepUpOk(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!readyToConfirm || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
    } catch (e) {
      setErr(e?.message || 'Action failed');
      setBusy(false);
      return;
    }
    setBusy(false);
  };

  const switchToCode = () => {
    setMode('code');
    setErr(null);
    setPassword('');
  };
  const switchToPassword = () => {
    setMode('password');
    setErr(null);
    setCode('');
    setCodeSent(false);
  };

  return (
    <div className="cam-backdrop" onClick={onClose} data-testid="confirm-modal">
      <div
        className={`cam-card ${destructive ? 'is-danger' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cam-title"
      >
        <button className="cam-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        <div className="cam-head">
          <div className={`cam-ico ${destructive ? 'is-danger' : ''}`}>
            <AlertTriangle size={20} strokeWidth={2.2} />
          </div>
          <h3 id="cam-title" className="cam-title">{title}</h3>
        </div>

        <div className="cam-msg">{message}</div>

        {/* Step-up */}
        {requirePassword && !stepUpOk && mode === 'password' && (
          <div className="cam-field">
            <label className="cam-label">
              <Lock size={12} /> Confirm your password
            </label>
            <input
              ref={firstInputRef}
              type="password"
              className="cam-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && password && handleStepUp()}
              placeholder="Your account password"
              autoComplete="current-password"
              data-testid="confirm-modal-password"
            />
            <div className="cam-row-between">
              <button
                type="button"
                className="cam-link"
                onClick={switchToCode}
                data-testid="switch-to-code"
              >
                Use email code instead
              </button>
              <button
                className="cam-btn cam-btn-ghost"
                onClick={handleStepUp}
                disabled={!password || busy}
                data-testid="confirm-modal-stepup-btn"
              >
                {busy ? 'Verifying…' : 'Verify password'}
              </button>
            </div>
          </div>
        )}

        {requirePassword && !stepUpOk && mode === 'code' && (
          <div className="cam-field">
            <label className="cam-label">
              <Mail size={12} /> Email me a 6-digit code
            </label>
            {!codeSent ? (
              <>
                <p className="cam-hint">
                  We'll email a single-use code to your inbox. It expires in 10 minutes.
                </p>
                <div className="cam-row-between">
                  {hasPassword && (
                    <button
                      type="button"
                      className="cam-link"
                      onClick={switchToPassword}
                    >
                      Use password instead
                    </button>
                  )}
                  <button
                    className="cam-btn cam-btn-ghost"
                    onClick={sendCode}
                    disabled={busy}
                    data-testid="send-code-btn"
                  >
                    {busy ? 'Sending…' : 'Send code'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  ref={firstInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="cam-input cam-input-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && code && handleStepUp()}
                  placeholder="123 456"
                  data-testid="confirm-modal-code"
                />
                <div className="cam-row-between">
                  <button
                    type="button"
                    className="cam-link"
                    onClick={sendCode}
                    disabled={busy}
                  >
                    Resend code
                  </button>
                  <button
                    className="cam-btn cam-btn-ghost"
                    onClick={handleStepUp}
                    disabled={code.length < 6 || busy}
                    data-testid="confirm-modal-verify-code-btn"
                  >
                    {busy ? 'Verifying…' : 'Verify code'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Typed confirmation */}
        {requireTypedText && (stepUpOk || !requirePassword) && (
          <div className="cam-field">
            <label className="cam-label">
              Type <code className="cam-word">{confirmWord}</code> to continue
            </label>
            <input
              ref={!requirePassword ? firstInputRef : null}
              type="text"
              className="cam-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && readyToConfirm && handleConfirm()}
              autoFocus={stepUpOk}
              data-testid="confirm-modal-word"
            />
          </div>
        )}

        {err && <div className="cam-err">{err}</div>}

        <div className="cam-actions">
          <button className="cam-btn cam-btn-ghost" onClick={onClose} disabled={busy} data-testid="confirm-modal-cancel">
            Cancel
          </button>
          <button
            className={`cam-btn ${destructive ? 'cam-btn-danger' : 'cam-btn-primary'}`}
            onClick={handleConfirm}
            disabled={!readyToConfirm || busy}
            data-testid="confirm-modal-confirm"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
